import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import moment from 'moment';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {getPublicProfilePath} from '../../../constants/routes';
import {FilterTabs, Input} from '../../../components';
import {EmptyState} from '../../../components/empty-state';
import {Heading, HeadingColor, HeadingLevel, HeadingVariant, HeadingWeight} from '../../../components/typography/heading';
import {Icons} from '../../../assets';
import {JobApplication, JobApplicationStatus} from '../../../models/job-application';
import {Opportunity} from '../../../models/opportunity';
import {RecommendedCandidateEntry} from '../../../services/opportunity';
import {useOpportunity} from '../../../contexts/opportunity.provider';
import {ApplicantCard} from './applicant-card.component';
import {RecommendedCandidateCard} from './recommended-candidate-card';

/** Source mode: where candidates come from (top-level segmentation). */
export enum ApplicantSource {
  APPLICATIONS = 'applications',
  DISCOVER_TALENT = 'discover',
  INVITED = 'invited',
}

/** Status filter within Applications mode only. */
export enum ApplicantTab {
  ALL = 'all',
  APPLIED = 'applied',
  REJECTED = 'rejected',
  SHORTLISTED = 'shortlisted',
}

interface ApplicantsPanelProps {
  opportunity: Opportunity;
}

const ITEMS_PER_PAGE = 6;
const SEARCH_DEBOUNCE_MS = 500;

const RECOMMENDED_ITEMS_PER_PAGE = 6;

export const ApplicantsPanel: React.FC<ApplicantsPanelProps> = ({opportunity}) => {
  const navigate = useNavigate();
  const {getJobApplications, getRecommendedCandidates, inviteCandidate} = useOpportunity();
  const [source, setSource] = useState<ApplicantSource>(ApplicantSource.APPLICATIONS);
  const [applicationsStatusTab, setApplicationsStatusTab] = useState<ApplicantTab>(ApplicantTab.APPLIED);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [invitedAccountIds, setInvitedAccountIds] = useState<Set<string>>(new Set());
  const [inviteErrors, setInviteErrors] = useState<Record<string, string>>({});
  const [invitingAccountId, setInvitingAccountId] = useState<string | null>(null);
  const [recommendedEntries, setRecommendedEntries] = useState<RecommendedCandidateEntry[]>([]);
  const [recommendedPage, setRecommendedPage] = useState(1);
  const [recommendedHasMore, setRecommendedHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isLoadingRecommended, setIsLoadingRecommended] = useState(false);
  const [isFetchingMoreRecommended, setIsFetchingMoreRecommended] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [invitedCount, setInvitedCount] = useState(0);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const recommendedLoaderRef = useRef<HTMLDivElement | null>(null);

  const [totalCounts, setTotalCounts] = useState<Record<ApplicantTab, number>>({
    [ApplicantTab.ALL]: 0,
    [ApplicantTab.APPLIED]: opportunity.applicationStats.applied || 0,
    [ApplicantTab.SHORTLISTED]: opportunity.applicationStats.shortlisted || 0,
    [ApplicantTab.REJECTED]: opportunity.applicationStats.rejected || 0,
  });

  useEffect(() => {
    setTotalCounts(prev => ({
      ...prev,
      [ApplicantTab.APPLIED]: opportunity.applicationStats.applied || 0,
      [ApplicantTab.SHORTLISTED]: opportunity.applicationStats.shortlisted || 0,
      [ApplicantTab.REJECTED]: opportunity.applicationStats.rejected || 0,
    }));
  }, [opportunity.applicationStats]);

  const baseTabLabels: Record<ApplicantTab, string> = {
    [ApplicantTab.ALL]: 'All',
    [ApplicantTab.APPLIED]: 'Applied',
    [ApplicantTab.SHORTLISTED]: 'Shortlisted',
    [ApplicantTab.REJECTED]: 'Rejected',
  };

  const applicationsTabs = useMemo<{key: ApplicantTab; label: string; status?: JobApplicationStatus}[]>(
    () => [
      {key: ApplicantTab.ALL, label: baseTabLabels[ApplicantTab.ALL]},
      {key: ApplicantTab.APPLIED, label: baseTabLabels[ApplicantTab.APPLIED], status: JobApplicationStatus.APPLIED},
      {key: ApplicantTab.SHORTLISTED, label: baseTabLabels[ApplicantTab.SHORTLISTED], status: JobApplicationStatus.ACCEPTED},
      {key: ApplicantTab.REJECTED, label: baseTabLabels[ApplicantTab.REJECTED], status: JobApplicationStatus.REJECTED},
    ],
    [],
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const fetchApplications = useCallback(
    async (pageNum = 1, append = false) => {
      try {
        if (append) setIsFetchingMore(true);
        else setIsLoading(true);

        const statusMap: Record<ApplicantTab, JobApplicationStatus | undefined> = {
          [ApplicantTab.ALL]: undefined,
          [ApplicantTab.APPLIED]: JobApplicationStatus.APPLIED,
          [ApplicantTab.SHORTLISTED]: JobApplicationStatus.ACCEPTED,
          [ApplicantTab.REJECTED]: JobApplicationStatus.REJECTED,
        };
        const effectiveStatus = source === ApplicantSource.INVITED ? JobApplicationStatus.INVITED : statusMap[applicationsStatusTab];

        const response = await getJobApplications(opportunity.id, {
          limit: ITEMS_PER_PAGE,
          page: pageNum,
          search: debouncedSearch || undefined,
          status: effectiveStatus,
        });

        setApplications(prev => (append ? [...prev, ...response.data] : response.data));
        setHasMore(response.hasMore);
        setPage(response.page);
        if (!append && response.total !== undefined) {
          if (source === ApplicantSource.INVITED) {
            setInvitedCount(response.total);
          } else if (applicationsStatusTab !== ApplicantTab.ALL) {
            setTotalCounts(prev => ({...prev, [applicationsStatusTab]: response.total}));
          }
        }
      } catch (error) {
        console.error('Error fetching job applications:', error);
      } finally {
        setIsLoading(false);
        setIsFetchingMore(false);
      }
    },
    [applicationsStatusTab, debouncedSearch, getJobApplications, opportunity.id, source],
  );

  const fetchRecommended = useCallback(
    async (pageNum = 1, append = false) => {
      try {
        if (append) setIsFetchingMoreRecommended(true);
        else setIsLoadingRecommended(true);

        const q = debouncedSearch.length >= 3 ? debouncedSearch : undefined;
        const response = await getRecommendedCandidates(opportunity.id, {
          limit: RECOMMENDED_ITEMS_PER_PAGE,
          page: pageNum,
          q,
        });

        setRecommendedEntries(prev => (append ? [...prev, ...response.data] : response.data));
        setRecommendedHasMore(response.hasMore);
        setRecommendedPage(response.page);
      } catch (error) {
        console.error('Error fetching recommended candidates:', error);
      } finally {
        setIsLoadingRecommended(false);
        setIsFetchingMoreRecommended(false);
      }
    },
    [debouncedSearch, getRecommendedCandidates, opportunity.id],
  );

  useEffect(() => {
    if (source === ApplicantSource.DISCOVER_TALENT) {
      setIsLoadingRecommended(true);
      fetchRecommended(1, false);
    } else {
      fetchApplications(1, false);
    }
  }, [applicationsStatusTab, fetchApplications, fetchRecommended, source]);

  const handleInvite = useCallback(
    async (entry: RecommendedCandidateEntry) => {
      const accountId = entry.candidate.accountId;
      if (!accountId) {
        toast.error('Candidate account not found.');
        return;
      }
      setInvitingAccountId(accountId);
      setInviteErrors(prev => {
        const next = {...prev};
        delete next[accountId];
        return next;
      });
      try {
        await inviteCandidate(opportunity.id, accountId);
        setInvitedAccountIds(prev => new Set(prev).add(accountId));
        toast.success('Invitation sent');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to send invitation.';
        const isAlreadyInvited = message.toLowerCase().includes('already') && message.toLowerCase().includes('invited');
        if (isAlreadyInvited) {
          setInvitedAccountIds(prev => new Set(prev).add(accountId));
          setInviteErrors(prev => {
            const next = {...prev};
            delete next[accountId];
            return next;
          });
          toast.info('Candidate was already invited.');
        } else {
          setInviteErrors(prev => ({...prev, [accountId]: message}));
          toast.error(message);
        }
      } finally {
        setInvitingAccountId(null);
      }
    },
    [inviteCandidate, opportunity.id],
  );

  useEffect(() => {
    const isDiscoverInfinite = source === ApplicantSource.DISCOVER_TALENT && recommendedHasMore && !isFetchingMoreRecommended;
    const isListInfinite = (source === ApplicantSource.APPLICATIONS || source === ApplicantSource.INVITED) && hasMore && !isFetchingMore;
    if (!isDiscoverInfinite && !isListInfinite) return;

    const observer = new IntersectionObserver(
      entries => {
        const target = entries[0];
        if (!target?.isIntersecting) return;
        if (source === ApplicantSource.DISCOVER_TALENT) {
          if (recommendedHasMore && !isFetchingMoreRecommended) fetchRecommended(recommendedPage + 1, true);
        } else {
          if (hasMore && !isFetchingMore) fetchApplications(page + 1, true);
        }
      },
      {threshold: 1.0},
    );

    const ref = source === ApplicantSource.DISCOVER_TALENT ? recommendedLoaderRef.current : loaderRef.current;
    if (ref) observer.observe(ref);
    return () => {
      if (ref) observer.unobserve(ref);
    };
  }, [
    fetchApplications,
    fetchRecommended,
    hasMore,
    isFetchingMore,
    isFetchingMoreRecommended,
    page,
    recommendedHasMore,
    recommendedPage,
    source,
  ]);

  const searchPlaceholder =
    source === ApplicantSource.DISCOVER_TALENT ? 'Search by name or skills (min 3 characters)' : 'Search applicants...';

  return (
    <div className="flex-1 flex flex-col">
      <div className="px-8 py-6">
        <div className="mb-2">
          <Heading
            level={HeadingLevel.H1}
            variant={HeadingVariant.PAGE_TITLE}
            weight={HeadingWeight.BOLD}
            color={HeadingColor.PRIMARY}
            className="text-2xl">
            {source === ApplicantSource.APPLICATIONS
              ? 'Applications'
              : source === ApplicantSource.DISCOVER_TALENT
              ? 'Recommended'
              : 'Invited'}
          </Heading>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4 border-b border-gray-200 pb-4">
          <button
            type="button"
            onClick={() => setSource(ApplicantSource.APPLICATIONS)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              source === ApplicantSource.APPLICATIONS ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            Applications
          </button>
          <button
            type="button"
            onClick={() => setSource(ApplicantSource.DISCOVER_TALENT)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              source === ApplicantSource.DISCOVER_TALENT ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            Recommended
          </button>
          <button
            type="button"
            onClick={() => setSource(ApplicantSource.INVITED)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              source === ApplicantSource.INVITED ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            Invited
          </button>
        </div>

        {source === ApplicantSource.APPLICATIONS && (
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex-1 min-w-[260px]">
              <FilterTabs
                options={applicationsTabs.map(t => ({label: t.label, value: t.key}))}
                value={applicationsStatusTab}
                onChange={tab => setApplicationsStatusTab(tab as ApplicantTab)}
              />
            </div>
            <div className="w-full md:w-auto md:ml-auto">
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full md:w-[360px] py-lg-2 rounded-pill text-sm"
                startEnhancer={<img src={Icons.MAGINFYING_BLACK_SEARCH_ICON} alt="" className="w-8 h-8" />}
              />
            </div>
          </div>
        )}

        {source === ApplicantSource.DISCOVER_TALENT && (
          <>
            <div className="mb-4">
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full max-w-md py-lg-2 rounded-pill text-sm"
                startEnhancer={<img src={Icons.MAGINFYING_BLACK_SEARCH_ICON} alt="" className="w-8 h-8" />}
              />
            </div>
            <div className="mb-4 px-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold text-gray-900">AI Matched Candidates</h2>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 border border-blue-200">
                  AI Ranked
                </span>
              </div>
              <p className="text-gray-600 text-sm mt-1">Ranked by AI based on role requirements and experience fit.</p>
            </div>
          </>
        )}

        {source === ApplicantSource.INVITED && (
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex-1 min-w-[200px] max-w-md">
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full py-lg-2 rounded-pill text-sm"
                startEnhancer={<img src={Icons.MAGINFYING_BLACK_SEARCH_ICON} alt="" className="w-8 h-8" />}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="p-6">
          {source === ApplicantSource.DISCOVER_TALENT ? (
            isLoadingRecommended ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
              </div>
            ) : recommendedEntries.length > 0 ? (
              <>
                <div className="max-w-6xl mx-auto">
                  <div className="grid gap-6 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
                    {recommendedEntries.map((entry, index) => (
                      <RecommendedCandidateCard
                        key={entry.candidate.id || entry.candidate.accountId || index}
                        rank={index + 1}
                        entry={entry}
                        requiredSkillNames={opportunity.requiredSkills?.map(s => s.name).filter(Boolean) ?? []}
                        inviteError={entry.candidate.accountId ? inviteErrors[entry.candidate.accountId] : undefined}
                        isInvited={entry.candidate.accountId ? invitedAccountIds.has(entry.candidate.accountId) : false}
                        isInviting={entry.candidate.accountId ? invitingAccountId === entry.candidate.accountId : false}
                        onInvite={entry.inviteEligible ? () => handleInvite(entry) : undefined}
                        onReview={
                          entry.application
                            ? () =>
                                navigate(`/opportunities/${opportunity.id}/applicants`, {
                                  state: {applicationId: entry.application?.id},
                                })
                            : undefined
                        }
                        onViewProfile={() => {
                          if (entry.candidate.accountId) {
                            const path = getPublicProfilePath(entry.candidate.accountId, {
                              candidateId: entry.candidate.id ?? entry.candidate.accountId,
                              opportunityId: opportunity.id,
                              source: 'recommended',
                            });
                            window.open(path, '_blank', 'noopener,noreferrer');
                          } else if (entry.application) {
                            const url = `/opportunities/${opportunity.id}/applicants?applicationId=${encodeURIComponent(
                              entry.application.id,
                            )}`;
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>
                {recommendedHasMore && (
                  <div ref={recommendedLoaderRef} className="flex justify-center mt-8 mb-6">
                    {isFetchingMoreRecommended ? (
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
                    ) : (
                      <p className="text-gray-500 text-sm">Scroll down to load more...</p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <EmptyState
                icon={Icons.NO_ACTIVITY}
                title="No recommended candidates found"
                description={
                  debouncedSearch.length >= 3
                    ? 'No candidates match your search. Try a different query or clear search to see suggestions based on job title and skills.'
                    : "We're finding top matches for you. Use search (min 3 characters) to discover candidates, or check back later."
                }
                className="py-16"
              />
            )
          ) : source === ApplicantSource.INVITED || source === ApplicantSource.APPLICATIONS ? (
            isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
              </div>
            ) : applications.length > 0 ? (
              <>
                <div className="max-w-6xl mx-auto">
                  <div className="grid gap-6 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
                    {applications.map((app, index) => {
                      const name = app.account.userName || app.account.firstName || app.account.displayName || '';
                      const email = app.account?.email || '';
                      const avatar = app.account?.profilePictureResource?.url;
                      const phone = `${app.account.phoneNumber?.countryCode ?? ''} ${app.account.phoneNumber?.phoneNumber ?? ''}`.trim();
                      const headline = app.account?.title ?? undefined;
                      const skills =
                        app.cred?.skills
                          ?.map((us: {skill?: {id?: string; name?: string}; id?: string}) => ({
                            id: us.skill?.id ?? us.id ?? '',
                            label: us.skill?.name ?? '',
                          }))
                          .filter((s: {label: string}) => s.label) ?? [];
                      return (
                        <ApplicantCard
                          key={app.id}
                          name={name}
                          email={email}
                          phone={phone}
                          imageUrl={avatar}
                          status={app.status}
                          auraScore={app.auraScore ?? 0}
                          appliedDate={moment(app.createdAt).format('MMMM D, YYYY')}
                          headline={headline}
                          skills={skills}
                          requiredSkillNames={opportunity.requiredSkills?.map(s => s.name).filter(Boolean) ?? []}
                          onButtonClick={() => {
                            if (source === ApplicantSource.INVITED && app.account?.id) {
                              const path = getPublicProfilePath(app.account.id, {
                                candidateId: app.account.id,
                                opportunityId: opportunity.id,
                                source: 'recommended',
                              });
                              window.open(path, '_blank', 'noopener,noreferrer');
                              return;
                            }
                            navigate(`/opportunities/${opportunity.id}/applicants`, {
                              state: {
                                hasMore,
                                jobApplications: applications,
                                page,
                                selectedIndex: index,
                                status: applicationsTabs.find(t => t.key === applicationsStatusTab)?.status,
                              },
                            });
                          }}
                        />
                      );
                    })}
                  </div>
                </div>

                {hasMore && (
                  <div ref={loaderRef} className="flex justify-center mt-8 mb-6">
                    {isFetchingMore ? (
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
                    ) : (
                      <p className="text-gray-500 text-sm">Scroll down to load more...</p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <EmptyState
                icon={Icons.NO_ACTIVITY}
                title={
                  source === ApplicantSource.INVITED
                    ? 'No invited candidates'
                    : applicationsStatusTab === ApplicantTab.ALL
                    ? 'No applicants found'
                    : `No ${baseTabLabels[applicationsStatusTab]?.toLowerCase() || 'applicants'} found`
                }
                description={
                  source === ApplicantSource.INVITED
                    ? 'Invited candidates will appear here once you send invitations from Recommended.'
                    : debouncedSearch
                    ? 'No applicants match your search. Try different keywords or clear the search to see all.'
                    : applicationsStatusTab === ApplicantTab.ALL
                    ? 'No applicants yet. Share your job link.'
                    : `There are no ${baseTabLabels[applicationsStatusTab]?.toLowerCase() || 'applicants'} for this opportunity yet.`
                }
                className="py-16"
              />
            )
          ) : null}
        </div>
      </div>
    </div>
  );
};
